import PropTypes from 'prop-types';
import {
  Fragment, useEffect, useRef, useState,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import MicroModal from 'micromodal';
import { Link } from 'next-translate-routes';

// Utils
import { cn } from 'utils';

// Styles
import styles from './AppartmentsBlock.module.css';

// Components
import Title from '../../atoms/Title/Title';
import ListBlock from '../../atoms/ListBlock/ListBlock';
import CarouselBlock from '../../atoms/CarouselBlock/CarouselBlock';
import Button from '../../atoms/Button/Button';
import RichTextContent from '../../atoms/RichTextContent/RichTextContent';
import { Picto } from '../../atoms/Picto/Picto';

// Constants
import { CONTACT_SLUG, MEDIUM_LONG_STAY, SHORT_STAY } from '../../../src/constants';

// Hooks
import useResponsiveQueries from '../../../src/hooks/useResponsiveQueries';
import MobileAppartments from './MobileAppartments';

const Lightbox = dynamic(() => import('../../organisms/Lightbox/Lightbox'), { ssr: false });
const Modal = dynamic(() => import('../../organisms/Modal/Modal'), { ssr: false });
const Tooltip = dynamic(() => import('../../atoms/Tooltip/Tooltip'), { ssr: false });

const VIRTUAL_VISIT_MODAL = 'virtualVisitModal';

function AppartmentsBlock({
  title,
  subtitle,
  apartments,
  isLongStay,
  isMediumStay,
  activity,
  displayContact,
  theme,
  residencePmsId,
  ctaLabel,
  ctaLink,
  className,
}) {
  const { t } = useTranslation();
  const [activeApartment, setActiveApartment] = useState(apartments[0]?.data?.code);
  const [displayVirtualVisit, setDisplayVirtualVisit] = useState('');
  const appartementSelectorContainerRef = useRef(null);
  const { isTablet } = useResponsiveQueries();

  useEffect(() => {
    if (appartementSelectorContainerRef.current) {
      let timeoutId;
      appartementSelectorContainerRef.current.addEventListener('scroll', () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          // Detect which child is near the left border of the container and select it
          const { children } = appartementSelectorContainerRef.current;
          const containerWidth = appartementSelectorContainerRef.current.offsetWidth;
          const containerScroll = appartementSelectorContainerRef.current.scrollLeft;
          const containerThreshold = containerWidth * 0.3;
          let nearestChild = 0;
          let nearestChildDistance = containerWidth;
          for (let i = 0; i < children.length; i += 1) {
            const child = children[i];
            const childDistance = Math.abs(child.offsetLeft - containerScroll);
            if (childDistance < nearestChildDistance && childDistance <= containerThreshold) {
              nearestChild = i;
              nearestChildDistance = childDistance;
            }
          }
          if (containerScroll === 0) {
            nearestChild = 1;
          }
          const nearestChildDatasetCode = children[nearestChild].dataset.code;
          setActiveApartment(nearestChildDatasetCode);
        }, 300);
      });
    }
  }, [appartementSelectorContainerRef]);

  const handleAppartementSelectorClick = (e) => {
    setActiveApartment(e.target.parentElement.dataset.code);
  };

  const handleAppartementSelectorArrowClick = () => {
    // add 100px to the right at the current appartementSelectorContainerRef scroll position
    appartementSelectorContainerRef.current.scrollTo({
      left: appartementSelectorContainerRef.current.scrollLeft + 100,
      behavior: 'smooth',
    });
  };

  /**
   * @param {string} name apartment type
   * @returns {void}
   */
  const handleChangeApartment = (type) => {
    setActiveApartment(type);
  };

  const handleVirtualVisitModal = (id) => {
    setDisplayVirtualVisit(id);
    MicroModal.show(id);
  };

  return !isTablet
    ? (
      <div id={subtitle} className={cn([styles.wrapper, styles[theme], className])}>
        <div className={styles.container}>
          <Title
            title={title}
            subtitle={subtitle}
            theme={theme}
            className={styles.title}
          />
          <div className={styles.containerApartment}>

            <div className={styles.apartmentSelector} ref={appartementSelectorContainerRef}>
              {apartments?.length ? apartments.map(({ data }, index) => (
                <Fragment key={data?.code}>
                  <div
                    className={cn([styles.apartment,
                      activeApartment === data?.code ? styles.active : '',
                      styles.desktop,
                    ])}
                  >
                    <button
                      className={styles.apartmentButton}
                      type="button"
                      onClick={() => handleChangeApartment(data?.code)}
                    >
                      {data?.label}
                      <Picto icon={activeApartment === data?.code ? 'minus' : 'plus'} className={styles.picto} />
                    </button>
                    <motion.div
                      initial={false}
                      animate={activeApartment === data?.code ? 'open' : 'collapsed'}
                      variants={{
                        collapsed: { height: 0, overflow: 'hidden' },
                        open: { height: 'auto' },
                      }}
                      transition={{ duration: 0.3 }}
                    >

                      <motion.div
                        variants={{
                          collapsed: { opacity: 0, transition: { duration: 0 } },
                          open: { opacity: 1, transition: { delay: 0.2, duration: 0.3 } },
                        }}
                      >
                        <div className={styles.apartmentInfo}>
                          {data?.size ? (
                            <div className={styles.area}>
                              {t('apartmentBlock.surface', { surface: data?.size })}
                            </div>
                          ) : null}
                          <div className={styles.priceContainer}>
                            {data?.minPrice > 0 && activity === MEDIUM_LONG_STAY ? (
                              <>
                                <span className={styles.price}>
                                  {t('apartmentBlock.priceMobile', { price: data?.minPrice })}
                                </span>
                                <span className={styles.priceContent}>
                                  {t('apartmentBlock.perMonth')}
                                  {isLongStay || isMediumStay ? (
                                    <Tooltip
                                      arrow={false}
                                      visible
                                      content={(
                                        <>
                                          {isLongStay
                                            ? (<div>{data?.taxesInformationsLongStay}</div>)
                                            : null}
                                          {!isLongStay && isMediumStay
                                            ? (<div>{data?.taxesInformationsMediumStay}</div>)
                                            : null}
                                        </>
                                          )}
                                    >
                                      <div>
                                        <Picto icon="information" className={styles.apartmentPicto} />
                                      </div>
                                    </Tooltip>
                                  ) : null}
                                </span>
                              </>
                            ) : null}

                            {data?.minPrice > 0 && activity === SHORT_STAY ? (
                              <span className={styles.price}>{t('apartmentBlock.priceNightMobile', { price: data?.minPrice })}</span>
                            ) : null}

                          </div>

                          {displayContact ? (
                            <Link className={styles.contactButton} href={`/${CONTACT_SLUG}#help=ORGANISERVISITE&residence=${residencePmsId}`}>
                              <Button
                                CTAType="primary"
                                usageContext={theme}
                              >
                                {t('apartmentBlock.contact')}
                              </Button>
                            </Link>
                          ) : null}
                        </div>
                      </motion.div>
                    </motion.div>
                  </div>
                  <div
                    className={cn([styles.apartmentSelectorButtons, styles.tablet])}
                    data-code={data?.code}
                    style={{ marginRight: index + 1 === apartments.length ? '150px' : '0' }}
                  >
                    <button
                      type="button"
                      onClick={handleAppartementSelectorClick}
                      className={cn([styles.apartmentSelectorButton,
                        activeApartment === data?.code ? styles.active : ''])}
                    >
                      {data?.label}
                    </button>
                  </div>
                </Fragment>
              )) : null}
            </div>
            <div className={styles.arrowRight}>
              <button type="button" aria-label="right" onClick={handleAppartementSelectorArrowClick}>
                <Picto icon="carouselButton" className={styles.pictoArrowRight} />
              </button>
            </div>

            <div className={cn([styles.apartmentInfo, styles.tablet])}>
              {apartments?.length ? apartments.map(({ data }) => (
                <Fragment key={data?.code}>
                  <AnimatePresence initial={false}>
                    <motion.div
                      initial="hidden"
                      animate={activeApartment === data?.code ? 'open' : 'hidden'}
                      exit="exit"
                      variants={{
                        hidden: {
                          opacity: 0,
                          height: 'auto', // Réduit la hauteur à zéro pour masquer l'élément
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          left: 0,
                          overflow: 'hidden', // Empêche le débordement de contenu lorsque la hauteur est réduite
                          transition: {
                            opacity: { duration: 0.3 },
                            height: {
                              duration: 0.3,
                              delay: 0.3,
                              ease: 'easeInOut',
                            },
                          },
                        },
                        open: {
                          opacity: 1,
                          height: 'auto', // L'élément prend sa hauteur complète lorsqu'il est visible
                          position: 'relative',
                          top: 0,
                          overflow: 'visible', // Permet le débordement de contenu lorsque la hauteur est complète
                          transition: {
                            opacity: { duration: 0.5, delay: 0.3 },
                            height: {
                              duration: 0.5,
                              ease: 'easeInOut',
                            },
                          },
                        },
                        exit: {
                          opacity: 0,
                          height: 0, // Retourne la hauteur à zéro lors de la sortie
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          left: 0,
                          overflow: 'hidden',
                          transition: {
                            opacity: { duration: 0.3 },
                            height: {
                              duration: 0.3,
                              delay: 0.3,
                              ease: 'easeInOut',
                            },
                          },
                        },
                      }}
                    >
                      <div className={styles.priceContainer}>
                        {data?.minPrice > 0 && activity === MEDIUM_LONG_STAY ? (
                          <>
                            <span className={styles.price}>{t('apartmentBlock.priceMobile', { price: data?.minPrice })}</span>
                            <span className={styles.priceContent}>
                              {t('apartmentBlock.perMonthMobile')}
                              {isLongStay || isMediumStay ? (
                                <Tooltip
                                  arrow={false}
                                  visible
                                  content={(
                                    <>
                                      {isLongStay
                                        ? (<div>{data?.taxesInformationsLongStay}</div>)
                                        : null}
                                      {!isLongStay && isMediumStay
                                        ? (<div>{data?.taxesInformationsMediumStay}</div>)
                                        : null}
                                    </>
                                )}
                                >
                                  <div>
                                    <Picto icon="information" className={styles.apartmentPicto} />
                                  </div>
                                </Tooltip>
                              ) : null}
                            </span>
                          </>
                        ) : null}

                        {data?.minPrice > 0 && activity === SHORT_STAY ? (
                          <span className={styles.price}>{t('apartmentBlock.priceNightMobile', { price: data?.minPrice })}</span>
                        ) : null}

                        {data?.size ? (
                          <div className={styles.area}>
                            {t('apartmentBlock.surface', { surface: data?.size })}
                          </div>
                        ) : null}
                      </div>

                      {displayContact ? (
                        <Link className={styles.contactButton} href={`/${CONTACT_SLUG}#help=ORGANISERVISITE&residence=${residencePmsId}`}>
                          <Button
                            CTAType="primary"
                            usageContext={theme}
                          >
                            {t('apartmentBlock.contact')}
                          </Button>
                        </Link>
                      ) : null}

                    </motion.div>
                  </AnimatePresence>
                </Fragment>
              )) : null}
            </div>

            <div className={styles.apartmentContent}>
              {apartments?.length ? apartments.map(({ data }) => (
                <Fragment key={data?.code}>
                  <motion.div
                    initial="hidden"
                    animate={activeApartment === data?.code ? 'open' : 'hidden'}
                    exit="exit"
                    variants={{
                      hidden: {
                        opacity: 0,
                        height: 'auto', // Réduit la hauteur à zéro pour masquer l'élément
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        left: 0,
                        overflow: 'hidden', // Empêche le débordement de contenu lorsque la hauteur est réduite
                        transition: {
                          opacity: { duration: 0.3 },
                          height: {
                            duration: 0.3,
                            delay: 0.3,
                            ease: 'easeInOut',
                          },
                        },
                      },
                      open: {
                        opacity: 1,
                        height: 'auto', // L'élément prend sa hauteur complète lorsqu'il est visible
                        position: 'relative',
                        top: 0,
                        overflow: 'visible', // Permet le débordement de contenu lorsque la hauteur est complète
                        transition: {
                          opacity: { duration: 0.5, delay: 0.3 },
                          height: {
                            duration: 0.5,
                            ease: 'easeInOut',
                          },
                        },
                      },
                      exit: {
                        opacity: 0,
                        height: 0, // Retourne la hauteur à zéro lors de la sortie
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        left: 0,
                        overflow: 'hidden',
                        transition: {
                          opacity: { duration: 0.3 },
                          height: {
                            duration: 0.3,
                            delay: 0.3,
                            ease: 'easeInOut',
                          },
                        },
                      },
                    }}
                  >
                    <Lightbox images={data?.medias} className={styles.lightbox}>
                      {(handleClickImage) => (
                        <>
                          <button
                            type="button"
                            className={cn([styles.main, styles.desktop, data?.medias.length === 1 ? styles.mainFull : ''])}
                            onClick={() => handleClickImage(0)}
                          >
                            {data?.medias[0]?.url ? (
                              <Image
                                src={data?.medias[0]?.url}
                                alt={data?.medias[0]?.description}
                                fill
                                sizes="50vw"
                              />
                            ) : null}
                          </button>
                          {data?.medias?.length > 1 ? (
                            <div className={cn([styles.row, styles.desktop])}>
                              {data?.medias.slice(1, 4).map(({ url, description }, index) => (
                                url ? (
                                  <button
                                    type="button"
                                    className={styles.small}
                                    key={url}
                                    onClick={() => handleClickImage(index + 1)}
                                  >
                                    <Image
                                      src={url}
                                      alt={description}
                                      fill
                                      sizes="16vw"
                                    />
                                  </button>
                                ) : null
                              ))}
                            </div>
                          ) : null}
                        </>
                      )}
                    </Lightbox>

                    {data?.medias?.length ? (
                      <div className={cn([styles.tablet, styles.carouselBlock])}>
                        <CarouselBlock medias={data?.medias} />
                      </div>
                    ) : null}

                    <div className={styles.contentWrapper}>
                      {data?.virtualVisit ? (
                        <Button
                          icon="turn360"
                          iconPosition="right"
                          CTAType="secondary"
                          usageContext={theme}
                          className={styles.tablet}
                          onClick={() => handleVirtualVisitModal(`${VIRTUAL_VISIT_MODAL}-${data?.label}`)}
                        >
                          {t('apartmentBlock.virtualVisit')}
                        </Button>
                      ) : null}
                      <div className={styles.contentContainer}>
                        <h3 className={styles.contentTitle}>
                          {data?.label}
                        </h3>
                        <ListBlock
                          className={styles.list}
                          list={data?.equipment}
                          theme={theme}
                        />
                      </div>
                      <div className={styles.contentContainer}>
                        <h3 className={styles.contentTitle}>
                          {activity === MEDIUM_LONG_STAY ? t('apartmentBlock.why') : null}
                          {activity === SHORT_STAY ? t('apartmentBlock.whyShortStay') : null}
                        </h3>
                        <RichTextContent
                          className={styles.description}
                          content={data?.longDescription}
                        />
                        {data?.virtualVisit ? (
                          <Button
                            icon="turn360"
                            iconPosition="right"
                            className={styles.desktop}
                            CTAType="secondary"
                            usageContext={theme}
                            onClick={() => handleVirtualVisitModal(`${VIRTUAL_VISIT_MODAL}-${data?.label}`)}
                          >
                            {t('apartmentBlock.virtualVisit')}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </motion.div>
                  <Modal
                    id={`${VIRTUAL_VISIT_MODAL}-${data?.label}`}
                    theme="virtualVisit"
                    fullScreen
                    hideCancelButton
                  >
                    {data?.virtualVisit && displayVirtualVisit === `${VIRTUAL_VISIT_MODAL}-${data?.label}` ? (
                      <iframe
                        title={`${data?.label}-${data?.virtualVisit}`}
                        src={data?.virtualVisit}
                        width="100%"
                        height="100%"
                      />
                    ) : null}
                  </Modal>
                </Fragment>
              )) : null}
            </div>

          </div>
        </div>
      </div>
    ) : (
      <MobileAppartments
        title={title}
        subtitle={subtitle}
        apartments={apartments}
        isLongStay={isLongStay}
        isMediumStay={isMediumStay}
        activity={activity}
        displayContact={displayContact}
        theme={theme}
        residencePmsId={residencePmsId}
        ctaLabel={ctaLabel}
        ctaLink={ctaLink}
      />
    );
}

AppartmentsBlock.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  apartments: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.shape({
      code: PropTypes.string,
      size: PropTypes.number,
      minPrice: PropTypes.number,
      taxesInformationsLongStay: PropTypes.string,
      taxesInformationsMediumStay: PropTypes.string,
      images: PropTypes.arrayOf(PropTypes.shape({
        data: PropTypes.shape({
          image: PropTypes.shape({
            url: PropTypes.string,
            description: PropTypes.string,
          }),
        }),
      })),
      equipment: PropTypes.arrayOf(PropTypes.shape({
        data: PropTypes.shape({
          label: PropTypes.string,
          images: PropTypes.shape({
            url: PropTypes.string,
            description: PropTypes.string,
          }),
        }),
      })),
    }),
  })),
  isLongStay: PropTypes.bool,
  isMediumStay: PropTypes.bool,
  activity: PropTypes.string,
  displayContact: PropTypes.bool,
  theme: PropTypes.oneOf(['light', 'dark', 'neutral', '']),
  residencePmsId: PropTypes.string,
  ctaLink: PropTypes.string,
  ctaLabel: PropTypes.string,
  className: PropTypes.string,
};

AppartmentsBlock.defaultProps = {
  title: '',
  subtitle: '',
  apartments: [],
  isLongStay: false,
  isMediumStay: false,
  activity: '',
  displayContact: false,
  theme: 'light',
  className: '',
  residencePmsId: '',
  ctaLink: null,
  ctaLabel: null,
};

export default AppartmentsBlock;
